import axios from 'axios';
import getJWT, { getEncodedToken } from './getJWT';
import type {
  AxiosRequestConfig,
  RawAxiosRequestHeaders,
  AxiosResponse
} from 'axios';

const defaultOrigin =
  window.VITE_API_BASE ||
  import.meta.env.VITE_API_BASE ||
  window.location.origin.replace(
    /^(https?:\/\/)(\w+)(\..*vinciworks.*\.com)$/,
    '$1portal$3'
  );

const defaultBase = defaultOrigin + '/api/content/v1';

type ClientAPIResponse<T> = {
  data: { results?: T[]; totalResults?: number };
  status: string;
  message: string;
  error: string;
};

export default async function callClientPortal<T>(
  endpoint: string,
  req?: AxiosRequestConfig & RequestInit,
  { base = defaultBase } = {}
): Promise<{
  data: ClientAPIResponse<T>;
  headers: Headers;
  response: AxiosResponse;
}> {
  const { tenantid: tenant } = getJWT();
  const url = base + endpoint;

  const contentType =
    req?.body instanceof FormData
      ? null
      : { 'Content-Type': 'application/json' };

  const headers = {
    accept: '*/*',
    Authorization: 'Bearer: ' + getEncodedToken(),
    'x-tenant-id': tenant,
    ...contentType,
    ...req?.headers
  } as RawAxiosRequestHeaders;

  try {
    const response = await axios({
      url,
      ...req,
      method: req?.method || 'GET',
      data: req?.body,
      headers
    });

    const responseData: ClientAPIResponse<T> = response.data;

    if (
      response.status >= 400 ||
      (responseData?.error &&
        (Array.isArray(responseData.error)
          ? responseData.error.length > 0
          : responseData.error))
    ) {
      const apiError =
        (typeof responseData?.error === 'string' && responseData.error) ||
        responseData?.message ||
        response.statusText;
      throw new Error(apiError);
    }

    return { data: responseData, headers: response.headers, response };
  } catch (e) {
    const code = e?.response?.status;

    const body = e.response?.data;
    const bodyError =
      (typeof body?.error === 'string' && body.error) || body?.message;
    e.message = (code ? `[${code}] ` : '') + (bodyError || e.message);

    throw e;
  }
}

declare global {
  interface Window {
    resetCustomization: () => Promise<void>;
  }
}
